import apiClient from "./api-client";
import type { EffectiveAccessContext } from "@medicorepay/shared";
import { isBackgroundDbWorkPaused } from "./background-db-work-pause";

// ═══════════════════════════════════════════════════════════════════════════
// WEB TENANT VALIDATOR SERVICE
// Validates tenant status for the PWA to prevent unauthorized use
// ═══════════════════════════════════════════════════════════════════════════

const REQUIRED_MODULE = "WEB_APP";

export interface TenantValidationResult {
  valid: boolean;
  tenantId: string;
  tenantName: string;
  tenantStatus: string;
  subscription: {
    id: string;
    status: string;
    planName: string;
    planType: string;
    features: string[];
    endDate: string | null;
    renewalDate: string | null;
  };
  enabledModules: string[];
  accessContext: EffectiveAccessContext;
  limits: {
    maxStores: number;
    maxUsers: number;
    currentStores: number;
    currentUsers: number;
  };
  validUntil: string;
  maxOfflineDays: number;
  validatedAt: string;
}

export interface TenantValidationStatus {
  isValid: boolean;
  isReadOnly: boolean;
  lastValidation: Date | null;
  daysOffline: number;
  requiresOnlineValidation: boolean;
  error?: string;
}

const STORAGE_KEYS = {
  LAST_VALIDATION: "tenant_last_validation",
  VALIDATION_DATA: "tenant_validation_data",
  MAX_OFFLINE_DAYS: "tenant_max_offline_days",
};

const DEFAULT_MAX_OFFLINE_DAYS = 3; // Web PWAs get shorter grace period

// Re-validation interval: 4 hours
const REVALIDATION_INTERVAL_MS = 4 * 60 * 60 * 1000;

class TenantValidatorService {
  private maxOfflineDays: number = DEFAULT_MAX_OFFLINE_DAYS;
  private validationListeners: Set<(status: TenantValidationStatus) => void> =
    new Set();
  private revalidationTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    const storedMaxDays = localStorage.getItem(STORAGE_KEYS.MAX_OFFLINE_DAYS);
    if (storedMaxDays) {
      this.maxOfflineDays = parseInt(storedMaxDays, 10);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PUBLIC METHODS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Validate tenant session on app startup
   */
  async validateOnAppStart(): Promise<TenantValidationStatus> {
    const lastValidation = this.getLastValidationTime();
    const daysOffline = this.getDaysSinceLastValidation(lastValidation);

    // If offline too long, enter read-only mode instead of full logout
    // Users can still view data but cannot create new invoices or modify data
    if (daysOffline > this.maxOfflineDays) {
      const status: TenantValidationStatus = {
        isValid: true,
        isReadOnly: true,
        lastValidation,
        daysOffline,
        requiresOnlineValidation: true,
        error: `Read-only mode. You've been offline for ${daysOffline} days. Connect to the internet to restore full access.`,
      };
      this.notifyListeners(status);
      return status;
    }

    // If online, perform server validation
    if (navigator.onLine) {
      return await this.performOnlineValidation();
    }

    // Offline but within grace period
    const status: TenantValidationStatus = {
      isValid: true,
      isReadOnly: false,
      lastValidation,
      daysOffline,
      requiresOnlineValidation: daysOffline > this.maxOfflineDays - 1,
    };
    this.notifyListeners(status);
    return status;
  }

  /**
   * Perform online validation against the server
   */
  async performOnlineValidation(): Promise<TenantValidationStatus> {
    try {
      const response = await apiClient.post<TenantValidationResult>(
        "/auth/validate-session",
      );

      const data = response.data;

      // Check if tenant has WEB_APP module enabled
      if (!data.enabledModules?.includes(REQUIRED_MODULE)) {
        const status: TenantValidationStatus = {
          isValid: false,
          isReadOnly: false,
          lastValidation: this.getLastValidationTime(),
          daysOffline: this.getDaysSinceLastValidation(
            this.getLastValidationTime(),
          ),
          requiresOnlineValidation: true,
          error:
            "Your subscription plan does not include Web App access. Please upgrade your plan.",
        };
        this.notifyListeners(status);
        return status;
      }

      // Store validation data
      this.storeValidationData(data);
      window.dispatchEvent(
        new CustomEvent("access-context-updated", {
          detail: data.accessContext,
        }),
      );

      if (data.maxOfflineDays) {
        this.maxOfflineDays = data.maxOfflineDays;
        localStorage.setItem(
          STORAGE_KEYS.MAX_OFFLINE_DAYS,
          data.maxOfflineDays.toString(),
        );
      }

      const status: TenantValidationStatus = {
        isValid: true,
        isReadOnly: false,
        lastValidation: new Date(),
        daysOffline: 0,
        requiresOnlineValidation: false,
      };
      this.notifyListeners(status);
      return status;
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        const status: TenantValidationStatus = {
          isValid: false,
          isReadOnly: false,
          lastValidation: this.getLastValidationTime(),
          daysOffline: this.getDaysSinceLastValidation(
            this.getLastValidationTime(),
          ),
          requiresOnlineValidation: true,
          error:
            error.response?.data?.message ||
            "Your access has been revoked. Please contact support.",
        };
        this.notifyListeners(status);
        return status;
      }

      // Network error — allow if within grace period
      const lastValidation = this.getLastValidationTime();
      const daysOffline = this.getDaysSinceLastValidation(lastValidation);

      if (daysOffline > this.maxOfflineDays) {
        const status: TenantValidationStatus = {
          isValid: true,
          isReadOnly: true,
          lastValidation,
          daysOffline,
          requiresOnlineValidation: true,
          error:
            "Read-only mode. Unable to validate session. Connect to the internet to restore full access.",
        };
        this.notifyListeners(status);
        return status;
      }

      const status: TenantValidationStatus = {
        isValid: true,
        isReadOnly: false,
        lastValidation,
        daysOffline,
        requiresOnlineValidation: false,
      };
      this.notifyListeners(status);
      return status;
    }
  }

  /**
   * Get current validation status without making API call
   */
  getCurrentStatus(): TenantValidationStatus {
    const lastValidation = this.getLastValidationTime();
    const daysOffline = this.getDaysSinceLastValidation(lastValidation);

    const cachedData = this.getCachedValidationData();
    const hasWebModule =
      cachedData?.enabledModules?.includes(REQUIRED_MODULE) ?? false;

    const isExpired = daysOffline > this.maxOfflineDays;
    const isReadOnly = isExpired && hasWebModule;

    return {
      isValid: hasWebModule, // Keep valid but read-only after expiry
      isReadOnly,
      lastValidation,
      daysOffline,
      requiresOnlineValidation: isExpired || !hasWebModule,
      error: isReadOnly
        ? `Read-only mode. You've been offline for ${daysOffline} days.`
        : !hasWebModule && cachedData
          ? "Your subscription plan does not include Web App access."
          : undefined,
    };
  }

  getCachedValidationData(): TenantValidationResult | null {
    const data = localStorage.getItem(STORAGE_KEYS.VALIDATION_DATA);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  clearValidationData(): void {
    this.stopPeriodicRevalidation();
    localStorage.removeItem(STORAGE_KEYS.LAST_VALIDATION);
    localStorage.removeItem(STORAGE_KEYS.VALIDATION_DATA);
  }

  subscribe(listener: (status: TenantValidationStatus) => void): () => void {
    this.validationListeners.add(listener);
    return () => this.validationListeners.delete(listener);
  }

  shouldShowOfflineWarning(): boolean {
    const lastValidation = this.getLastValidationTime();
    if (!lastValidation) return true;
    const daysOffline = this.getDaysSinceLastValidation(lastValidation);
    return daysOffline >= this.maxOfflineDays - 1;
  }

  getRemainingOfflineDays(): number {
    const lastValidation = this.getLastValidationTime();
    const daysOffline = this.getDaysSinceLastValidation(lastValidation);
    return Math.max(0, this.maxOfflineDays - daysOffline);
  }

  hasModule(moduleCode: string): boolean {
    const cachedData = this.getCachedValidationData();
    return cachedData?.enabledModules?.includes(moduleCode) ?? false;
  }

  getEnabledModules(): string[] {
    const cachedData = this.getCachedValidationData();
    return cachedData?.enabledModules ?? [];
  }

  /**
   * Start periodic re-validation every 4 hours
   */
  startPeriodicRevalidation(): void {
    this.stopPeriodicRevalidation();
    this.revalidationTimer = setInterval(async () => {
      if (isBackgroundDbWorkPaused()) return;
      if (navigator.onLine) {
        console.log("[TenantValidator] Running periodic re-validation...");
        const status = await this.performOnlineValidation();
        if (!status.isValid) {
          console.warn("[TenantValidator] Re-validation failed:", status.error);
        }
      } else {
        const currentStatus = this.getCurrentStatus();
        if (!currentStatus.isValid) {
          this.notifyListeners(currentStatus);
        }
      }
    }, REVALIDATION_INTERVAL_MS);
  }

  stopPeriodicRevalidation(): void {
    if (this.revalidationTimer) {
      clearInterval(this.revalidationTimer);
      this.revalidationTimer = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PRIVATE METHODS
  // ─────────────────────────────────────────────────────────────────────────

  private getLastValidationTime(): Date | null {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_VALIDATION);
    if (!stored) return null;
    const date = new Date(stored);
    return isNaN(date.getTime()) ? null : date;
  }

  private getDaysSinceLastValidation(lastValidation: Date | null): number {
    if (!lastValidation) return Infinity;
    const diffMs = new Date().getTime() - lastValidation.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  private storeValidationData(data: TenantValidationResult): void {
    const now = new Date().toISOString();
    localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, now);
    localStorage.setItem(STORAGE_KEYS.VALIDATION_DATA, JSON.stringify(data));
  }

  private notifyListeners(status: TenantValidationStatus): void {
    this.validationListeners.forEach((listener) => listener(status));
  }
}

export const tenantValidator = new TenantValidatorService();
export { TenantValidatorService };
